/**
 * Vulnerability Scan Handlers Hook
 *
 * Provides scan action handlers for vulnerabilities including:
 * - Rescanning affected assets
 *
 * @module sections/vulnerabilities/hooks/actions
 */

import { toast } from 'sonner';

import { useBulkCapabilityJob } from '@/hooks/useCapability';
import { HackerOneSubmitParams } from '@/sections/vulnerabilities/components/modals/HackerOneSubmitModal';
import { VulnerabilityScanHandlers } from '@/sections/vulnerabilities/types/action.types';
import { Risk } from '@/types';

/**
 * Custom hook for vulnerability scan handlers
 * Orchestrates scan execution for vulnerabilities and their affected assets
 *
 * @param onScanComplete - Optional callback to invoke after scans are submitted
 * @returns Object containing scan handler functions
 */
export const useVulnerabilityScanHandlers = (
  onScanComplete?: () => void
): VulnerabilityScanHandlers => {
  const { mutateAsync: runJobs } = useBulkCapabilityJob();

  /**
   * Trigger rescans on entities affected by selected vulnerabilities
   *
   * Process:
   * 1. Send risk keys directly to the job endpoint
   * 2. Backend automatically finds entities that have HAS_VULNERABILITY relationship to each risk
   *    - This includes Assets with direct vulnerabilities (Asset -[HAS_VULNERABILITY]-> Risk)
   *    - And Ports with vulnerabilities (Port -[HAS_VULNERABILITY]-> Risk)
   * 3. Backend creates appropriate scan jobs for each affected entity type
   * 4. Jobs are filtered to only use the same capability that originally found the vulnerability
   *
   * @param vulnerabilities - Vulnerabilities to rescan affected entities for
   */
  const handleRescan = async (vulnerabilities: Risk[]): Promise<void> => {
    try {
      // Build scan jobs with risk keys - backend handles finding affected entities
      // The backend's risk handler (pkg/services/job/service.go:118-157) will:
      // 1. Query for all entities with HAS_VULNERABILITY to each risk
      // 2. Create appropriate jobs for each entity type (Asset, Port, etc.)
      // 3. Filter jobs to match the risk's source capability for targeted rescanning
      const jobs = vulnerabilities.map(vulnerability => ({
        assetKey: vulnerability.key, // Actually a risk key - backend detects #risk# prefix
        // No capabilities specified = backend uses risk's source capability
      }));

      // Trigger scans via existing proven mechanism
      // Note: useBulkCapabilityJob mutation handles success toast notification
      await runJobs(jobs);

      // Invalidate queries to refresh vulnerability table
      onScanComplete?.();
    } catch (error) {
      // useBulkCapabilityJob already surfaces an error toast on the all-failed path (its only rejection path); avoid double-toasting.
      console.error('[useVulnerabilityScanHandlers] Rescan failed:', error);
    }
  };

  /**
   * Re-validate hunt-discovered (agent-reported) vulnerabilities with Marcus.
   *
   * Hunt findings are filed by an agent through the backend `report_new_risk`
   * tool and carry no originating capability to re-run. The agent that
   * re-examines them is cato-agent, so we explicitly request it against each
   * risk key. The backend risk-rescan path (pkg/services/job/service.go) wires
   * risk-targeting agents like cato-agent to the #risk# key, and Job.Put sets
   * config.manual=true, which bypasses the cato match gates for this on-demand
   * run.
   *
   * @param vulnerabilities - Hunt findings to re-validate with Marcus (Cato)
   */
  const handleRescanWithMarcus = async (
    vulnerabilities: Risk[]
  ): Promise<void> => {
    try {
      const jobs = vulnerabilities.map(vulnerability => ({
        assetKey: vulnerability.key, // Risk key - backend detects #risk# prefix
        capabilities: ['cato-agent'],
      }));

      await runJobs(jobs);

      onScanComplete?.();
    } catch (error) {
      // useBulkCapabilityJob already surfaces an error toast on the all-failed path (its only rejection path); avoid double-toasting.
      console.error(
        '[useVulnerabilityScanHandlers] Rescan with Marcus failed:',
        error
      );
    }
  };

  /**
   * Submit selected vulnerabilities to a HackerOne bug bounty program
   *
   * Process:
   * 1. Build job for each vulnerability with hackerone-submit capability
   * 2. Include manual trigger flag and team_handle in config
   * 3. Submit via useBulkCapabilityJob
   *
   * @param vulnerabilities - Vulnerabilities to submit
   * @param params - HackerOne submission parameters (team_handle required)
   */
  const handleSubmitToHackerOne = async (
    vulnerabilities: Risk[],
    params: HackerOneSubmitParams
  ): Promise<void> => {
    try {
      const config: Record<string, string> = {
        manual: 'true',
        team_handle: params.teamHandle,
      };
      if (params.title) {
        config.title = params.title;
      }
      if (params.severityRating) {
        config.severity_rating = params.severityRating;
      }
      if (params.vulnerabilityInformation) {
        config.vulnerability_information = params.vulnerabilityInformation;
      }
      if (
        params.structuredScopeId !== undefined &&
        params.structuredScopeId !== ''
      ) {
        config.structured_scope_id = params.structuredScopeId;
      }
      if (params.weaknessId !== undefined && params.weaknessId !== '') {
        config.weakness_id = params.weaknessId;
      }
      if (params.attachmentS3Keys && params.attachmentS3Keys.length > 0) {
        config.attachment_s3_keys = params.attachmentS3Keys.join(',');
      }

      const jobs = vulnerabilities.map(vulnerability => ({
        assetKey: vulnerability.key,
        capabilities: ['hackerone-submit'],
        config,
      }));

      await runJobs(jobs);
      onScanComplete?.();
    } catch (error) {
      // useBulkCapabilityJob already surfaces an error toast on the all-failed path (its only rejection path); avoid double-toasting.
      console.error(
        '[useVulnerabilityScanHandlers] HackerOne submit failed:',
        error
      );
    }
  };
  return {
    handleRescan,
    handleRescanWithMarcus,
    handleSubmitToHackerOne,
  };
};
